import { HttpContextToken, HttpErrorResponse, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { catchError, tap } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { AuthService } from '@/app/auth/shared/services/auth.service';
import { ToastService } from '../services/toast.service';
import { toUserFacingErrorDetail } from '../utils/api-error-toast.util';

interface ApiResponse<T = any> {
  isSuccess: boolean;
  statusCode: number;
  result: T | null;
  errors: any[];
  version: string;
  environment: string;
}

export const SKIP_API_TOAST = new HttpContextToken<boolean>(() => false);

export const ApiToastInterceptor: HttpInterceptorFn = (req, next) => {
  const messageService = inject(MessageService);
  const translate = inject(TranslateService);
  const authService = inject(AuthService);
  const skipApiToast =
    req.context.get(SKIP_API_TOAST) || req.url.includes('/notifications/negotiate');
  const showErrorToast = (errors?: unknown[], rawMessage?: unknown) => {
    if (skipApiToast) return;

    const defaultSummary = translate.instant('doktar_app.common.error') || 'Error';
    const defaultDetail =
      translate.instant('doktar_app.common_errors.someting_went_wrong_title') || 'Something went wrong';
    const detail = toUserFacingErrorDetail(errors, rawMessage) ?? defaultDetail;

    messageService.add({ severity: 'error', summary: defaultSummary, detail, life: ToastService.DEFAULT_LIFE });
  };

  return next(req).pipe(
    tap((event) => {
      if (!(event instanceof HttpResponse)) return;

      // JSON responses
      if (event.body && typeof event.body === 'object' && !(event.body instanceof Blob)) {
        const body: any = event.body as any;
        if (Object.prototype.hasOwnProperty.call(body, 'isSuccess')) {
          const apiBody = body as ApiResponse;
          // Skip auth-related statuses when user is not logged in
          if (apiBody.statusCode === 401 || apiBody.statusCode === 403 || !authService.isAuthenticated()) return;
          if (apiBody.isSuccess === false) {
            showErrorToast(apiBody.errors);
          }
        }
        return;
      }

      // Blob responses that may contain JSON error
      if (event.body instanceof Blob) {
        const reader = new FileReader();
        reader.onload = () => {
          try {
            const text = reader.result as string;
            const parsed: any = JSON.parse(text);
            if (parsed && typeof parsed === 'object' && 'isSuccess' in parsed) {
              const parsedResponse = parsed as ApiResponse;
              if (parsedResponse.statusCode === 401 || parsedResponse.statusCode === 403) return;
              if (parsedResponse.isSuccess === false) {
                showErrorToast(parsedResponse.errors);
              }
            }
          } catch {
            // Not JSON; ignore
          }
        };
        reader.readAsText(event.body);
      }
    }),
    catchError((error) => {
      if (skipApiToast) {
        return throwError(() => error);
      }

      // Show generic toast for HTTP errors except 401/403
      if (error instanceof HttpErrorResponse) {
        if (error.status !== 401 && error.status !== 403) {
          const errorsArr = Array.isArray(error.error?.errors) ? (error.error.errors as unknown[]) : undefined;
          const rawMessage =
            typeof error.error?.message === 'string'
              ? error.error.message
              : typeof error.error === 'string'
                ? error.error
                : undefined;
          showErrorToast(errorsArr, rawMessage);
        }
      } else {
        showErrorToast();
      }
      return throwError(() => error);
    })
  );
};
